import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { useSEOScript } from '@/hooks/useSEOScript';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');
  const relatedArticles = getRelatedArticles(id || '', 4);

  usePageMeta(
    article ? article.title : '文章',
    article ? article.excerpt : '乐鱼体育新闻资讯站文章详情'
  );

  useSEOScript(article ? {
    type: 'Article',
    data: {
      headline: article.title,
      description: article.excerpt,
      image: article.imageUrl,
      datePublished: article.publishDate,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      publisher: {
        '@type': 'Organization',
        name: '乐鱼体育',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://www.leyusports.com/article/${article.id}`,
      },
    },
  } : null);

  useSEOScript(article ? {
    type: 'BreadcrumbList',
    data: {
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://www.leyusports.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.category,
          item: `https://www.leyusports.com/category/${article.categorySlug}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
        },
      ],
    },
  } : null);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">文章不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.category, href: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-700 mb-4">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {(article.readCount / 10000).toFixed(1)}万阅读
                </span>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
                [&_p]:mb-5 [&_p]:leading-relaxed [&_p]:text-foreground-700
                [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-foreground-900
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200/70">
              <span className="text-sm font-medium text-foreground-600">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                  <h2 className="text-xl font-bold text-foreground-900">相关推荐</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}